import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Eye } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { KPICard } from "@/ui/KPICard";
import { Card } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { ProgressBar } from "@/ui/ProgressBar";
import { SegmentedFilter, type FilterOption } from "@/ui/SegmentedFilter";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { Modal, Field, fieldInputClass } from "@/ui/modals/Modal";
import { LoadingState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatPercent } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import { can, isAgencyAdmin, MOCK_USER } from "@/lib/permissions";
import type { Priority, Task, TaskStatus, TeamMember } from "@/types";
import { useCurrentUser } from "@/features/auth/auth.hooks";
import { useClients } from "@/features/clients/clients.hooks";
import { useTeamMembers } from "@/features/team/team.hooks";
import { ClientCombobox } from "@/ui/ClientCombobox";
import { useArchiveTask, useCreateTask, useMyTasks, useTasks, useUpdateTaskStatus, useUpdateProgress } from "./tasks.hooks";
import { getEffectiveProgress, getTaskProgressTone, getTaskProgressLabelKey, type TaskInput } from "./tasks.adapter";

type Filter = "all" | TaskStatus;

export function TasksPage() {
  const { data: currentUser } = useCurrentUser();
  const effectiveUser = currentUser ?? MOCK_USER;

  if (isAgencyAdmin(effectiveUser)) {
    return <AdminTasksView />;
  }
  return <MemberTasksView />;
}

// ─── Admin full view ───────────────────────────────────────────────────────────

function AdminTasksView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { PRIORITY, TEAM_ROLE } = useLabels();
  const filters: FilterOption<Filter>[] = [
    { value: "all", label: t("tasks.filters.all") },
    { value: "TODO", label: t("tasks.filters.notStarted") },
    { value: "IN_PROGRESS", label: t("tasks.filters.inProgress") },
    { value: "ON_HOLD", label: t("tasks.filters.blocked") },
    { value: "DONE", label: t("tasks.filters.completed") },
  ];
  const { tasks, isLoading, isError, refetch } = useTasks();
  const createTask = useCreateTask();
  const archiveTask = useArchiveTask();
  const { clients } = useClients();
  const { members, source: teamSource } = useTeamMembers();
  const { data: currentUser } = useCurrentUser();
  const effectiveUser = currentUser ?? MOCK_USER;
  const [filter, setFilter] = useState<Filter>("all");
  const [toDelete, setToDelete] = useState<Task | undefined>();
  const [addOpen, setAddOpen] = useState(false);

  const assignableMembers = useMemo(
    () => members.filter((m) => (teamSource === "mock" ? m.status === "ACTIVE" : m.status === "ACTIVE" && m.account?.isActive)),
    [members, teamSource],
  );
  const visible = useMemo(() => (filter === "all" ? tasks : tasks.filter((task) => task.status === filter)), [tasks, filter]);
  const stats = useMemo(() => {
    const done = tasks.filter((t) => t.status === "DONE").length;
    const inProgress = tasks.filter((t) => t.status === "IN_PROGRESS").length;
    const pending = tasks.filter((t) => t.status === "TODO" || t.status === "ON_HOLD").length;
    const rate = tasks.length ? (done / tasks.length) * 100 : 0;
    return { done, inProgress, pending, rate };
  }, [tasks]);

  const onCreate = async (input: TaskInput) => {
    try {
      await createTask.mutateAsync(input);
      toast.success(t("tasks.assignedSuccess"));
      setAddOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.createFailed")));
    }
  };

  const onArchiveConfirm = async () => {
    if (!toDelete) return;
    try {
      await archiveTask.mutateAsync(toDelete.id);
      toast.success(t("tasks.archived"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.archiveFailed")));
    } finally {
      setToDelete(undefined);
    }
  };

  const columns: Column<Task>[] = [
    { key: "title", header: t("tasks.columns.task"), cell: (task) => <span className="font-semibold text-text">{task.title}</span> },
    { key: "client", header: t("tasks.columns.client"), cell: (task) => (task.clientName ? <Badge tone="info">{task.clientName}</Badge> : <span className="text-text3">-</span>) },
    {
      key: "assignee",
      header: t("tasks.columns.assignee"),
      cell: (task) => (
        <div className="flex items-center gap-1.5">
          <span>{task.assignee ?? "-"}</span>
          {task.assigneeRole && <Badge tone={TEAM_ROLE[task.assigneeRole].tone}>{TEAM_ROLE[task.assigneeRole].label}</Badge>}
        </div>
      ),
    },
    { key: "priority", header: t("tasks.columns.priority"), cell: (task) => <Badge tone={PRIORITY[task.priority].tone} dot>{PRIORITY[task.priority].label}</Badge> },
    {
      key: "progress",
      header: t("tasks.columns.progress"),
      cell: (task) => {
        const pct = getEffectiveProgress(task);
        return (
          <div className="w-36">
            <ProgressBar value={pct} tone={getTaskProgressTone(task)} />
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-text3">{t(getTaskProgressLabelKey(task.status))}</span>
              <span className="text-[10px] text-text3">{pct}%</span>
            </div>
          </div>
        );
      },
    },
    { key: "due", header: t("tasks.columns.due"), cell: (task) => <span className="text-text2">{task.dueDate ?? "-"}</span> },
    {
      key: "actions", header: t("common.actions"), align: "right", cell: (task) => (
        <div className="flex items-center gap-1 justify-end">
          <Button variant="ghost" size="xs" onClick={() => navigate(`/tasks/${task.id}`)}><Eye className="h-3 w-3" /></Button>
          <Button variant="ghost" size="xs" className="text-danger" onClick={() => setToDelete(task)}><Trash2 className="h-3 w-3" /></Button>
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t("tasks.title")}
        subtitle={t("tasks.subtitle", { count: tasks.length, done: stats.done, inProgress: stats.inProgress })}
        actions={can(effectiveUser, "tasks.create") ? <Button size="sm" onClick={() => setAddOpen(true)}><Plus className="h-3 w-3" /> {t("tasks.add")}</Button> : null}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <KPICard label={t("tasks.kpis.done")} value={stats.done} accent="linear-gradient(90deg,#22c87a,#1aad65)" />
        <KPICard label={t("tasks.kpis.inProgress")} value={stats.inProgress} accent="linear-gradient(90deg,#5B6CF0,#4a5be0)" />
        <KPICard label={t("tasks.kpis.pending")} value={stats.pending} accent="linear-gradient(90deg,#f0a030,#d4891a)" />
        <KPICard label={t("tasks.kpis.completionRate")} value={formatPercent(stats.rate, 0)} accent="linear-gradient(90deg,#9b72f0,#7d50d4)" />
      </div>

      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}

      <div className="mb-3.5">
        <SegmentedFilter options={filters} value={filter} onChange={setFilter} />
      </div>

      <Card>
        {isLoading ? (
          <LoadingState rows={6} />
        ) : (
          <DataTable columns={columns} rows={visible} rowKey={(task) => task.id} empty={{ title: t("tasks.emptyTitle") }} />
        )}
      </Card>

      <AddTaskModal open={addOpen} onClose={() => setAddOpen(false)} onSave={onCreate} clients={clients} members={assignableMembers} />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(undefined)}
        title={t("tasks.archiveTitle")}
        message={t("tasks.archiveMessage", { title: toDelete?.title })}
        confirmLabel={t("common.archive")}
        onConfirm={onArchiveConfirm}
      />
    </PageContainer>
  );
}

// ─── Member workspace ─────────────────────────────────────────────────────────

function MemberTasksView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { PRIORITY } = useLabels();
  const filters: FilterOption<Filter>[] = [
    { value: "all", label: t("tasks.filters.all") },
    { value: "TODO", label: t("tasks.filters.todo") },
    { value: "IN_PROGRESS", label: t("tasks.filters.inProgress") },
    { value: "ON_HOLD", label: t("tasks.filters.blocked") },
    { value: "DONE", label: t("tasks.filters.done") },
  ];
  const { tasks, isLoading, isError, refetch } = useMyTasks();
  const updateStatus = useUpdateTaskStatus();
  const updateProgress = useUpdateProgress();
  const [filter, setFilter] = useState<Filter>("all");
  const [progressInputs, setProgressInputs] = useState<Record<string, string>>({});

  const visible = useMemo(() => (filter === "all" ? tasks : tasks.filter((task) => task.status === filter)), [tasks, filter]);

  const getProgress = (task: Task) => progressInputs[task.id] ?? String(getEffectiveProgress(task));
  const setProgress = (taskId: string, val: string) => setProgressInputs((prev) => ({ ...prev, [taskId]: val }));

  const onStatus = async (task: Task, status: TaskStatus) => {
    try {
      await updateStatus.mutateAsync({ id: task.id, status });
      toast.success(t("tasks.statusUpdated"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.updateStatusFailed")));
    }
  };

  const onProgressUpdate = async (task: Task) => {
    const val = progressInputs[task.id];
    if (val === undefined) return;
    const n = Math.max(0, Math.min(100, Number(val)));
    try {
      await updateProgress.mutateAsync({ id: task.id, progress: n });
      toast.success(t("tasks.progress.updated"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.updateStatusFailed")));
    }
  };

  const columns: Column<Task>[] = [
    { key: "title", header: t("tasks.columns.task"), cell: (task) => <span className="font-semibold text-text">{task.title}</span> },
    { key: "client", header: t("tasks.columns.client"), cell: (task) => (task.clientName ? <Badge tone="info">{task.clientName}</Badge> : <span className="text-text3">-</span>) },
    {
      key: "priority",
      header: t("tasks.columns.priority"),
      cell: (task) => (
        <Badge tone={task.priority === "HIGH" ? "danger" : task.priority === "MEDIUM" ? "warning" : "default"} dot>
          {PRIORITY[task.priority].label}
        </Badge>
      ),
    },
    {
      key: "progress",
      header: t("tasks.columns.progress"),
      cell: (task) => (
        <div className="flex items-center gap-1.5">
          <div className="w-16"><ProgressBar value={getEffectiveProgress(task)} tone={getTaskProgressTone(task)} /></div>
          {task.status !== "DONE" && (
            <>
              <input
                type="number"
                min={0}
                max={100}
                value={getProgress(task)}
                onChange={(e) => setProgress(task.id, e.target.value)}
                className="w-12 rounded border border-border bg-bg px-1 py-0.5 text-[11px] text-text text-center"
              />
              <Button variant="ghost" size="xs" onClick={() => onProgressUpdate(task)} disabled={updateProgress.isPending}>%</Button>
            </>
          )}
        </div>
      ),
    },
    { key: "due", header: t("tasks.columns.due"), cell: (task) => <span className="text-text2">{task.dueDate ?? "-"}</span> },
    {
      key: "actions",
      header: t("tasks.columns.updateStatus"),
      align: "right",
      cell: (task) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="xs" onClick={() => navigate(`/tasks/${task.id}`)}><Eye className="h-3 w-3" /></Button>
          {task.status !== "IN_PROGRESS" && task.status !== "DONE" && (
            <Button variant="ghost" size="xs" onClick={() => onStatus(task, "IN_PROGRESS")}>{t("tasks.markInProgress")}</Button>
          )}
          {task.status !== "ON_HOLD" && task.status !== "DONE" && (
            <Button variant="ghost" size="xs" className="text-warning" onClick={() => onStatus(task, "ON_HOLD")}>{t("tasks.markBlocked")}</Button>
          )}
          {task.status !== "DONE" && (
            <Button variant="ghost" size="xs" className="text-success" onClick={() => onStatus(task, "DONE")}>{t("tasks.markDone")}</Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader title={t("tasks.myWorkspaceTitle")} subtitle={t("tasks.myWorkspaceSubtitle", { count: tasks.length })} />

      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}

      <div className="mb-3.5">
        <SegmentedFilter options={filters} value={filter} onChange={setFilter} />
      </div>

      <Card>
        {isLoading ? (
          <LoadingState rows={6} />
        ) : (
          <DataTable columns={columns} rows={visible} rowKey={(task) => task.id} empty={{ title: t("tasks.myTasksEmpty") }} />
        )}
      </Card>
    </PageContainer>
  );
}

// ─── Add task modal (admin only) ──────────────────────────────────────────────

function AddTaskModal({
  open,
  onClose,
  onSave,
  clients,
  members,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (input: TaskInput) => void;
  clients: { id: string; name: string }[];
  members: TeamMember[];
}) {
  const { t } = useTranslation();
  const { PRIORITY, TEAM_ROLE } = useLabels();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [expectedResult, setExpectedResult] = useState("");
  const [doNotDo, setDoNotDo] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [teamMemberId, setTeamMemberId] = useState("");
  const [priority, setPriority] = useState<Priority>("MEDIUM");
  const [dueDate, setDueDate] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!title.trim()) {
      setError(t("tasks.form.titleRequired"));
      return;
    }
    const member = members.find((item) => item.id === teamMemberId);
    onSave({
      title: title.trim(),
      description: description || undefined,
      instructions: instructions || undefined,
      expectedResult: expectedResult || undefined,
      doNotDo: doNotDo || undefined,
      clientId: clientId || undefined,
      clientName: clientName || undefined,
      teamMemberId: member?.id,
      assignee: member?.name,
      priority,
      status: "TODO",
      dueDate: dueDate || undefined,
    });
    setTitle("");
    setDescription("");
    setInstructions("");
    setExpectedResult("");
    setDoNotDo("");
    setClientId("");
    setClientName("");
    setError("");
    setTeamMemberId("");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("tasks.form.addTitle")}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>{t("common.cancel")}</Button>
          <Button size="sm" onClick={submit}>{t("common.create")}</Button>
        </>
      }
    >
      <Field label={t("tasks.form.title")} required error={error}>
        <input className={fieldInputClass} value={title} onChange={(event) => setTitle(event.target.value)} />
      </Field>
      <Field label={t("tasks.form.description")}>
        <textarea className={fieldInputClass} rows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
      </Field>
      <Field label={t("tasks.form.instructions")}>
        <textarea className={fieldInputClass} rows={3} value={instructions} onChange={(event) => setInstructions(event.target.value)} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("tasks.form.expectedResult")}>
          <textarea className={fieldInputClass} rows={2} value={expectedResult} onChange={(event) => setExpectedResult(event.target.value)} />
        </Field>
        <Field label={t("tasks.form.doNotDo")}>
          <textarea className={fieldInputClass} rows={2} value={doNotDo} onChange={(event) => setDoNotDo(event.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("tasks.form.client")}>
          <ClientCombobox
            options={clients}
            value={clientId}
            onChange={(id, name) => { setClientId(id); setClientName(name); }}
            nullable
          />
        </Field>
        <Field label={t("tasks.assignToMember")}>
          <select className={fieldInputClass} value={teamMemberId} onChange={(event) => setTeamMemberId(event.target.value)}>
            <option value="">{members.length ? t("tasks.form.unassigned") : t("tasks.noAssignableMembers")}</option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name} - {TEAM_ROLE[member.role].label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {!members.length && (
        <div className="mb-4 rounded-sm border border-warning/20 bg-warning-dim px-3 py-2 text-[11.5px] text-warning">
          {t("tasks.memberCannotReceiveTasks")}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("tasks.form.priority")} required>
          <select className={fieldInputClass} value={priority} onChange={(event) => setPriority(event.target.value as Priority)}>
            {(Object.keys(PRIORITY) as Priority[]).map((item) => <option key={item} value={item}>{PRIORITY[item].label}</option>)}
          </select>
        </Field>
        <Field label={t("tasks.form.dueDate")}>
          <input type="date" className={fieldInputClass} value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
